import {
  Alert,
  Typography,
  Divider,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useEffect, useMemo, useState } from 'react'
import {
  useConfiguration,
  useDepartments,
  useCancelInvoice,
  useCreateFinalInvoice,
  useCreateInvoice,
  useUpdateInvoice,
  useSyncInvoiceBillingName,
  useDecideInvoiceDiscountApproval,
  useCustomer,
  useOrders,
  useOutlets,
  useServiceTypes,
} from '@/api/hooks'
import type { CustomerSummary, Invoice } from '@/api/types'
import { useAuthStore } from '@/auth/authStore'
import { BillLines, type BillLine } from '@/components/BillLines'
import { CustomerPicker } from '@/components/CustomerPicker'
import { DiscountField } from '@/components/DiscountField'
import {
  ErrorBanner,
  formatMobile,
  formatMoney,
  isoDate,
} from '@/components/common'

/**
 * Where the business supplies from unless somebody says otherwise. Nearly every
 * bill it raises is within the state, and typing the same answer onto each one
 * is how a blank gets left on a document that has to declare it.
 */
const DEFAULT_PLACE_OF_SUPPLY = 'Tamil Nadu'

/**
 * Two ways to raise an invoice: the usual one, built from the order so it picks
 * up approved changes automatically, and a manual one for anything the order
 * does not cover.
 */
export function NewInvoiceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState(0)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>New invoice</DialogTitle>
      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ px: 3 }}>
        <Tab label="Final invoice from an order" />
        <Tab label="Quick invoice" />
      </Tabs>
      {tab === 0 ? <FinalInvoiceForm onClose={onClose} /> : <QuickInvoiceForm onClose={onClose} />}
    </Dialog>
  )
}

/**
 * Corrects an invoice before it goes out.
 *
 * <p>Only a draft: once issued, the invoice is the customer's document and the
 * way back is to cancel it or raise a credit note, both of which leave a
 * trail. The same form that raised it is what corrects it, so the lines,
 * discount and dates read the same on the way back in.</p>
 */
export function EditInvoiceDialog({
  invoice,
  onClose,
}: {
  invoice: Invoice | null
  onClose: () => void
}) {
  return (
    <Dialog open={Boolean(invoice)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Correct {invoice?.invoiceNumber}</DialogTitle>
      {/* Said before anything is typed, because a document already sent is a
          document the customer has a copy of. The correction is recorded
          against whoever makes it. */}
      {invoice && invoice.status !== 'DRAFT' ? (
        <Alert severity="warning" sx={{ mx: 3, mt: 1 }}>
          {invoice.invoiceNumber} has already been issued
          {invoice.invoiceType === 'PROFORMA'
            ? '. Send the customer the corrected copy so the two agree.'
            : '. Correcting a tax invoice the customer holds a copy of is recorded against your name; where it has been filed, a credit note is the safer route.'}
        </Alert>
      ) : null}
      {invoice ? <QuickInvoiceForm invoice={invoice} onClose={onClose} /> : null}
    </Dialog>
  )
}

function FinalInvoiceForm({ onClose }: { onClose: () => void }) {
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSummary | null>(null)
  const [orderSearch, setOrderSearch] = useState('')
  const [orderTerm, setOrderTerm] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setOrderTerm(orderSearch.trim()), 250)
    return () => clearTimeout(timer)
  }, [orderSearch])
  const { data: orders } = useOrders({
    size: 100,
    customerId: selectedCustomer?.id || undefined,
    q: orderTerm || undefined,
  })
  const create = useCreateFinalInvoice()

  const [orderId, setOrderId] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(() => isoDate(0))
  const [dueDate, setDueDate] = useState(() => isoDate(7))
  const [interState, setInterState] = useState(false)
  // The venue's own charges - power, cleaning, recoveries - can go to whoever
  // is paying for the hall rather than onto the catering bill.
  const [separateAmenities, setSeparateAmenities] = useState(false)
  const [notes, setNotes] = useState('')

  const order = orders?.content?.find((candidate) => candidate.id === orderId)

  return (
    <>
      <DialogContent>
        <ErrorBanner error={create.error} />
        <Alert severity="info" sx={{ mb: 2 }}>
          Built from the order snapshot including approved change requests, so the figures match what
          was contracted.
        </Alert>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          {/* Narrows the order list below - a customer with several bookings,
              or an order number half-remembered, both find it faster than
              scrolling a list of a hundred. */}
          <Grid item xs={12} md={6}>
            <CustomerPicker
              value={selectedCustomer}
              onChange={(customer) => {
                setSelectedCustomer(customer)
                setOrderId('')
              }}
              label="Find by customer"
              helperText="Optional — narrows the order list below"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Find by order number"
              value={orderSearch}
              onChange={(event) => {
                setOrderSearch(event.target.value)
                setOrderId('')
              }}
              helperText="Optional — matches anywhere in the order number"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              required
              label="Order"
              value={orderId}
              onChange={(event) => setOrderId(event.target.value)}
            >
              {(orders?.content ?? []).map((candidate) => (
                <MenuItem key={candidate.id} value={candidate.id}>
                  {candidate.orderNumber} — {candidate.customerName} ·{' '}
                  {formatMoney(candidate.totalAmount)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Invoice date"
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Due date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch checked={interState} onChange={(event) => setInterState(event.target.checked)} />
              }
              label="Inter-state (IGST)"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={separateAmenities}
                  onChange={(event) => setSeparateAmenities(event.target.checked)}
                />
              }
              label="Bill amenities separately (electricity, hall cleaning, recoveries — 18%)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Grid>
        </Grid>

        {order ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            {order.orderNumber}: order value {formatMoney(order.totalAmount)}, received{' '}
            {formatMoney(order.paidAmount)}, balance {formatMoney(order.balanceAmount)}.
          </Alert>
        ) : null}
        <CustomerDetailsCard customerId={order?.customerId} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!orderId || create.isPending}
          onClick={() =>
            create.mutate(
              {
                orderId,
                invoiceDate,
                dueDate,
                interState,
                separateAmenitiesInvoice: separateAmenities,
                notes: notes || undefined,
              },
              { onSuccess: onClose },
            )
          }
        >
          Raise final invoice
        </Button>
      </DialogActions>
    </>
  )
}

/** One line of the money summary: a label on the left, the figure on the right. */
function Summary({ label, value, strong }: { label: string; value: number; strong?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.25 }}>
      <Typography variant="body2" fontWeight={strong ? 700 : 400}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={strong ? 700 : 400}>
        {formatMoney(value, true)}
      </Typography>
    </Stack>
  )
}

/**
 * What a customer is, read back once they are known - so the office sees
 * who is actually about to be billed before committing to it, rather than
 * just the name and mobile a picker's own dropdown showed on the way in.
 */
function CustomerDetailsCard({ customerId }: { customerId?: string }) {
  const { data: customer } = useCustomer(customerId)
  if (!customer) return null

  const address = [
    customer.billingAddressLine1,
    customer.billingAddressLine2,
    customer.billingCity,
    customer.billingState,
    customer.billingPostalCode,
  ]
    .filter((part) => part && part.trim())
    .join(', ')

  return (
    <Alert severity="info" icon={false} sx={{ mt: 1.5 }}>
      <Typography variant="body2" fontWeight={600}>
        {customer.title ? `${customer.title} ` : ''}
        {customer.name}
        {customer.companyName ? ` · ${customer.companyName}` : ''}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {customer.customerCode} · {formatMobile(customer.mobile)}
        {customer.email ? ` · ${customer.email}` : ''}
        {customer.gstin ? ` · GSTIN ${customer.gstin}` : ''}
      </Typography>
      {address ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {address}
        </Typography>
      ) : null}
      {customer.outstandingBalance > 0 ? (
        <Typography variant="caption" color="warning.main" display="block">
          Already owes {formatMoney(customer.outstandingBalance)} across other orders
        </Typography>
      ) : null}
      {!customer.enabled ? (
        <Typography variant="caption" color="error.main" display="block">
          Disabled — likely a duplicate record
        </Typography>
      ) : null}
    </Alert>
  )
}

/**
 * What is printed as the billed-to name freezes the day a bill is raised,
 * apart from the customer record it was taken from - so a correction made to
 * that record afterwards (a misspelling caught late, a company name typed
 * wrong) does not reach a bill already on file on its own. This offers to
 * retake it, showing the new name before it is applied.
 */
function BillingNameSync({
  invoice,
  liveCustomer,
}: {
  invoice: Invoice
  liveCustomer?: CustomerSummary
}) {
  const sync = useSyncInvoiceBillingName()
  const [confirming, setConfirming] = useState(false)
  const [syncedTo, setSyncedTo] = useState<string | null>(null)

  if (!liveCustomer || !invoice.billingName) return null
  const preferred = (
    liveCustomer.companyName?.trim() ||
    `${liveCustomer.title ? `${liveCustomer.title} ` : ''}${liveCustomer.name}`
  ).trim()
  if (
    !preferred ||
    syncedTo === preferred ||
    preferred.toLowerCase() === invoice.billingName.trim().toLowerCase()
  ) {
    return null
  }

  return (
    <>
      <Alert
        severity="info"
        icon={false}
        sx={{ mt: 1 }}
        action={
          <Button color="inherit" size="small" onClick={() => setConfirming(true)}>
            Change
          </Button>
        }
      >
        Billed as "{invoice.billingName}" — the customer record now reads "{preferred}".
      </Alert>
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Change billed-to name</DialogTitle>
        <DialogContent>
          <ErrorBanner error={sync.error} />
          <Typography variant="body2">
            {invoice.invoiceNumber} is billed as <strong>{invoice.billingName}</strong>. Change it to{' '}
            <strong>{preferred}</strong>, as the customer record now reads?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Keep it</Button>
          <Button
            variant="contained"
            disabled={sync.isPending}
            onClick={() =>
              sync.mutate(invoice.id, {
                onSuccess: () => {
                  setSyncedTo(preferred)
                  setConfirming(false)
                },
              })
            }
          >
            Use "{preferred}"
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

/**
 * A discount parked beyond the preparer's own authority, shown with a way
 * through it rather than left as a dead end - what the request was for, and
 * the same approve or turn down a manager would otherwise reach from
 * Settings &gt; Approvals.
 */
function PendingInvoiceDiscount({ invoice }: { invoice: Invoice }) {
  const decide = useDecideInvoiceDiscountApproval()
  if (!invoice.pendingDiscountAmount) return null

  return (
    <Alert
      severity="warning"
      sx={{ mt: 1 }}
      action={
        <Stack direction="row" spacing={1}>
          <Button
            color="inherit"
            size="small"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ id: invoice.id, approved: false })}
          >
            Turn down
          </Button>
          <Button
            color="inherit"
            size="small"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ id: invoice.id, approved: true })}
          >
            Approve
          </Button>
        </Stack>
      }
    >
      <ErrorBanner error={decide.error} />
      A discount of {formatMoney(invoice.pendingDiscountAmount)} (
      {invoice.pendingDiscountPercent}%) is waiting on approval
      {invoice.pendingDiscountReason ? `: ${invoice.pendingDiscountReason}` : ''}
      {invoice.pendingDiscountRecommendedBy ? ` — recommended by ${invoice.pendingDiscountRecommendedBy}` : ''}
    </Alert>
  )
}

/**
 * A bill raised straight onto a customer, with no order behind it.
 *
 * <p>For the work that never goes through a booking - a bulk takeaway collected
 * the same morning, a party in the restaurant, a small outdoor job. A banquet is
 * not offered: a hall booking carries a slot, a rent and a meter, all of which
 * live on the order, so it is always billed from there.</p>
 */
function QuickInvoiceForm({
  invoice,
  onClose,
}: {
  /** The draft being corrected. Absent, a new bill is raised. */
  invoice?: Invoice
  onClose: () => void
}) {
  // Both scoped to the business being worked in: the API answers for the
  // company in force, and switching companies throws the cache away. A group
  // running two of them was otherwise offered the wrong one's outlets.
  const { data: outlets } = useOutlets()
  const { data: serviceTypes } = useServiceTypes()
  const create = useCreateInvoice()
  const update = useUpdateInvoice()
  const editing = Boolean(invoice)
  const hasRole = useAuthStore((state) => state.hasRole)
  const isAdmin = hasRole('SUPER_ADMIN') || hasRole('MANAGEMENT')
  // A rate typed while raising a counter sale is the price actually charged,
  // not a correction - this only holds a company to what it decided once an
  // invoice already exists. New lines added while correcting one are held to
  // it too, which is why this does not simply check "is this a new line".
  const { data: settings } = useConfiguration()
  const rateEditAllowed =
    (settings ?? []).find((one) => one.key === 'billing.invoice.rate.edit')?.value !== 'false'
  const ratesEditable = !editing || rateEditAllowed

  // Seeded from the invoice being corrected, so the field reads who it was
  // raised for rather than opening blank and asking the search to be done
  // again for a customer already chosen once.
  const { data: seedCustomer } = useCustomer(invoice?.customerId)
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerSummary | null>(null)
  useEffect(() => {
    if (seedCustomer && !selectedCustomer) setSelectedCustomer(seedCustomer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedCustomer])
  const customerId = selectedCustomer?.id ?? ''
  const [outletId, setOutletId] = useState(invoice?.outletId ?? '')
  const [serviceType, setServiceType] = useState(invoice?.serviceType ?? '')
  const [invoiceDate, setInvoiceDate] = useState(() => invoice?.invoiceDate ?? isoDate(0))
  const [dueDate, setDueDate] = useState(() => invoice?.dueDate ?? isoDate(7))
  const [placeOfSupply, setPlaceOfSupply] = useState(
    invoice?.placeOfSupply ?? DEFAULT_PLACE_OF_SUPPLY,
  )
  // A sale over the counter is rung up and handed over. Where the line of
  // business is one that goes out to the customer, the master says so and the
  // question starts on delivery instead.
  const [fulfilmentMode, setFulfilmentMode] = useState(invoice?.fulfilmentMode ?? 'PICKUP')
  const [deliveryAddress, setDeliveryAddress] = useState(invoice?.deliveryAddress ?? '')
  // Agreed off the bill, not off a line: "give me five per cent" is said about
  // the bill. The server spreads it across the lines so each keeps its own rate
  // of tax.
  const [discountMode, setDiscountMode] = useState<'PERCENT' | 'AMOUNT'>(
    invoice && invoice.discountAmount > 0 ? 'AMOUNT' : 'PERCENT',
  )
  const [discountValue, setDiscountValue] = useState(
    invoice && invoice.discountAmount > 0 ? String(invoice.discountAmount) : '',
  )
  const [discountReason, setDiscountReason] = useState(invoice?.discountReason ?? '')
  const [discountRecommendedBy, setDiscountRecommendedBy] = useState(invoice?.discountRecommendedBy ?? '')
  const [interState, setInterState] = useState(Boolean(invoice && invoice.igstAmount > 0))
  // Handed over with the compliments of the house. Priced out all the same, so
  // what was given away is on record and can be totalled at the year end.
  const [complimentary, setComplimentary] = useState(
    invoice?.invoiceType === 'COMPLIMENTARY',
  )
  // Whose budget it comes out of. Asked for only where something is being
  // given away, and required there: the server refuses to issue one that does
  // not say, because the register exists to total a year by department.
  const [departmentId, setDepartmentId] = useState(invoice?.departmentId ?? '')
  const { data: departments } = useDepartments()
  const [notes, setNotes] = useState(invoice?.notes ?? '')
  // Old data, entered as it stood - never for a bill raised today, which
  // keeps its place in this company's own run of numbers. Admin only, and
  // only on a new invoice: once one exists its number is not retyped here.
  const [manualNumbering, setManualNumbering] = useState(false)
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [lines, setLines] = useState<BillLine[]>(() =>
    (invoice?.lines ?? []).map((line) => ({
      key: line.id,
      menuItemId: line.referenceId ?? '',
      item: null,
      description: line.description,
      quantity: String(line.quantity),
      unit: line.unit,
      unitPrice: String(line.unitPrice),
      taxRatePercent: String(line.taxRatePercent ?? 5),
      // A saved line's rate is always the figure after tax was already taken
      // out of it, whatever it started as - stating that plainly here stops
      // the server taking it out a second time, which it would otherwise do
      // on any company that ordinarily enters its prices GST-inclusive.
      priceInclusiveTax: false,
    })),
  )

  /**
   * What this business can be billed for without an order.
   *
   * <p>Read from its own service master rather than a list written in here, so
   * a line of business the office added is billable and one it withdrew is not.
   * A hall booking is left out: it carries a slot, a rent and a meter, all of
   * which live on the order, so it is always billed from there.</p>
   */
  const billable = useMemo(
    () => (serviceTypes ?? []).filter((type) => type.active && type.spaceType !== 'HALL'),
    [serviceTypes],
  )

  // Falls to the first thing this business actually sells, rather than to a
  // code that may mean nothing here.
  useEffect(() => {
    if (editing) return
    if (billable.length > 0 && !billable.some((type) => type.code === serviceType)) {
      setServiceType(billable[0].code)
    }
  }, [billable, serviceType, editing])

  // Bulk takeaway and outdoor catering are marked in the master as going out to
  // the customer; a counter sale is not. Only the opening answer - a shop that
  // sends fifty parcels out says so, and so does one that hands them over.
  const chosen = billable.find((type) => type.code === serviceType)
  useEffect(() => {
    if (editing) return
    setFulfilmentMode(chosen?.needsDeliveryAddress ? 'DELIVERY' : 'PICKUP')
  }, [chosen?.code, chosen?.needsDeliveryAddress, editing])

  const delivered = fulfilmentMode === 'DELIVERY'

  const usable = useMemo(
    () => lines.filter((line) => line.description.trim() && Number(line.quantity) > 0),
    [lines],
  )

  // Indicative only — the server recomputes tax and rounding on save.
  const gross = usable.reduce(
    (sum, line) => sum + Number(line.quantity || 0) * Number(line.unitPrice || 0),
    0,
  )
  const discount = Math.min(
    discountMode === 'PERCENT'
      ? (gross * Number(discountValue || 0)) / 100
      : Number(discountValue || 0),
    gross,
  )
  // Spread by value, which is what the server does, so the screen and the bill
  // agree on the tax where the lines are taxed at different rates.
  const kept = gross > 0 ? 1 - discount / gross : 1
  const preview = usable.reduce(
    (running, line) => {
      const amount = Number(line.quantity || 0) * Number(line.unitPrice || 0) * kept
      const rate = Number(line.taxRatePercent || 0)
      // A rate that already carries its GST discloses what is in it rather
      // than having tax added on top of a figure that already includes it.
      const taxable = line.priceInclusiveTax && rate > 0 ? amount / (1 + rate / 100) : amount
      const tax = (taxable * rate) / 100
      return { taxable: running.taxable + taxable, tax: running.tax + tax }
    },
    { taxable: 0, tax: 0 },
  )

  return (
    <>
      <DialogContent>
        <ErrorBanner error={create.error ?? update.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={5}>
            <CustomerPicker value={selectedCustomer} onChange={setSelectedCustomer} required />
            <CustomerDetailsCard customerId={customerId} />
            {invoice ? <BillingNameSync invoice={invoice} liveCustomer={seedCustomer} /> : null}
            {invoice ? <PendingInvoiceDiscount invoice={invoice} /> : null}
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              required
              label="Outlet"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
            >
              {(outlets ?? []).map((outlet) => (
                <MenuItem key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              required
              label="Service"
              value={serviceType}
              onChange={(event) => setServiceType(event.target.value)}
              helperText="A hall booking is billed from its order"
            >
              {billable.map((service) => (
                <MenuItem key={service.code} value={service.code}>
                  {service.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Invoice date"
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Due date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="Place of supply"
              value={placeOfSupply}
              onChange={(event) => setPlaceOfSupply(event.target.value)}
              helperText="Where it is supplied unless said otherwise"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Switch checked={interState} onChange={(event) => setInterState(event.target.checked)} />
              }
              label="IGST"
            />
          </Grid>
          {/* Given away. The lines are still priced - what it was worth is the
              whole point of recording it - and the document says nothing is
              payable. It takes its number from its own series, so the tax
              invoice run stays the unbroken one the GST return accounts for. */}
          <Grid item xs={6} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={complimentary}
                  onChange={(event) => setComplimentary(event.target.checked)}
                />
              }
              label="Complimentary"
            />
          </Grid>
          {complimentary ? (
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                required
                label="Given away by"
                value={departmentId}
                onChange={(event) => setDepartmentId(event.target.value)}
                helperText="Whose budget it comes out of"
              >
                {(departments ?? []).map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          ) : null}

          {/* A counter sale is rung up and handed over, but not always: a shop
              that takes an order for fifty parcels and sends them out has no
              order behind the bill and nowhere else to write the address. */}
          <Grid item xs={12} md={delivered ? 4 : 12}>
            <TextField
              select
              fullWidth
              label="Handed over or sent"
              value={fulfilmentMode}
              onChange={(event) => setFulfilmentMode(event.target.value)}
              helperText={
                delivered ? 'A vehicle goes out' : 'The customer collects it'
              }
            >
              <MenuItem value="PICKUP">Customer collects</MenuItem>
              <MenuItem value="DELIVERY">Delivered</MenuItem>
            </TextField>
          </Grid>
          {delivered ? (
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Delivery address"
                value={deliveryAddress}
                onChange={(event) => setDeliveryAddress(event.target.value)}
                helperText="Where it went — a delivery with nowhere to go is found out on the day"
              />
            </Grid>
          ) : null}

          {/* Old data, typed in as it stood - not a bill raised today, which
              takes its number from this company's own run and no other. */}
          {isAdmin && !editing ? (
            <>
              <Grid item xs={12} md={manualNumbering ? 4 : 12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={manualNumbering}
                      onChange={(event) => {
                        setManualNumbering(event.target.checked)
                        if (!event.target.checked) setInvoiceNumber('')
                      }}
                    />
                  }
                  label="Old data — enter the document number"
                />
              </Grid>
              {manualNumbering ? (
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Invoice number"
                    value={invoiceNumber}
                    onChange={(event) => setInvoiceNumber(event.target.value)}
                    helperText="Exactly as it stood before — this company's own numbering carries on from where it is, untouched"
                  />
                </Grid>
              ) : null}
            </>
          ) : null}
        </Grid>

        <BillLines
          lines={lines}
          setLines={setLines}
          title="Lines"
          helper={
            ratesEditable
              ? 'Find the item, say how many, add it. The rate comes off the catalogue and stays editable.'
              : 'Find the item, say how many, add it. Rates are fixed by the catalogue for this company.'
          }
          ratesEditable={ratesEditable}
        />

        {usable.length > 0 ? (
          <>
            {/* Under the total, because that is where it is agreed: so much off
                the bill, as a percentage of it or as money. */}
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <TextField
                select
                size="small"
                label="Discount"
                value={discountMode}
                onChange={(event) =>
                  setDiscountMode(event.target.value as 'PERCENT' | 'AMOUNT')
                }
                sx={{ width: 150 }}
              >
                <MenuItem value="PERCENT">Percent</MenuItem>
                <MenuItem value="AMOUNT">Amount</MenuItem>
              </TextField>
              {discountMode === 'PERCENT' ? (
                <DiscountField
                  value={discountValue}
                  onChange={setDiscountValue}
                  onReason={setDiscountReason}
                  helperText={
                    discount > 0
                      ? `${formatMoney(discount)} off ${formatMoney(gross)}`
                      : 'Spread across the lines, so each keeps its own rate of tax'
                  }
                />
              ) : (
                <TextField
                  size="small"
                  type="number"
                  label="Off the bill"
                  value={discountValue}
                  onChange={(event) => setDiscountValue(event.target.value)}
                  inputProps={{ min: 0, 'aria-label': 'Discount off the bill' }}
                  helperText={
                    discount > 0
                      ? `${formatMoney(discount)} off ${formatMoney(gross)}`
                      : "Capped at the discount master's highest rate"
                  }
                  sx={{ width: 240 }}
                />
              )}
              {Number(discountValue || 0) > 0 ? (
                <>
                  <TextField
                    size="small"
                    label="Discount reason"
                    required
                    value={discountReason}
                    onChange={(event) => setDiscountReason(event.target.value)}
                    sx={{ width: 220 }}
                  />
                  <TextField
                    size="small"
                    label="Recommended by"
                    value={discountRecommendedBy}
                    onChange={(event) => setDiscountRecommendedBy(event.target.value)}
                    sx={{ width: 200 }}
                  />
                </>
              ) : null}
            </Stack>

            {/* What the bill comes to, in the order a bill states it: the
                items, what came off, what is taxed, the tax, and the sum. The
                rate each line is taxed at comes off the catalogue, so it is
                worked out here rather than typed anywhere. */}
            <Stack alignItems="flex-end" sx={{ mt: 2 }}>
              <Box sx={{ minWidth: 280 }}>
                <Summary label="Items" value={gross} />
                {discount > 0 ? (
                  <Summary
                    label={`Less discount${gross > 0 ? ` (${((discount / gross) * 100).toFixed(2)}%)` : ''}`}
                    value={-discount}
                  />
                ) : null}
                <Summary label="Taxable" value={preview.taxable} />
                <Summary label="GST" value={preview.tax} />
                <Divider sx={{ my: 0.5 }} />
                <Summary label="Total" value={preview.taxable + preview.tax} strong />
              </Box>
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              The server recomputes the tax and the rounding when the invoice is saved.
            </Typography>
          </>
        ) : null}

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={
            !customerId
            || !outletId
            || !serviceType
            || (delivered && !deliveryAddress.trim())
            || usable.length === 0
            || create.isPending
            || update.isPending
          }
          onClick={() => {
            const body = {
                // A quick bill is a tax invoice unless it is being given away,
                // and then it is a document of its own kind on its own
                // numbering. A proforma is raised against an order, where there
                // is something to propose against.
                invoiceType: complimentary ? 'COMPLIMENTARY' : 'TAX_INVOICE',
                customerId,
                outletId,
                serviceType,
                invoiceDate,
                dueDate,
                placeOfSupply: placeOfSupply || undefined,
                fulfilmentMode,
                deliveryAddress: delivered ? deliveryAddress.trim() : undefined,
                discountPercent:
                  discountMode === 'PERCENT' ? Number(discountValue || 0) : undefined,
                discountAmount:
                  discountMode === 'AMOUNT' ? Number(discountValue || 0) : undefined,
                discountReason: discountReason.trim() || undefined,
                discountRecommendedBy: discountRecommendedBy.trim() || undefined,
                interState,
                notes: notes || undefined,
                departmentId: complimentary ? departmentId || undefined : undefined,
                // No HSN or SAC is typed here: what a line is declared under is
                // the service's SAC or the product's own HSN, off the master,
                // and it is put on the printed document from there.
                lines: usable.map((line) => ({
                  // A line naming a dish is food; one typed by hand is the
                  // service being billed. Nobody is asked to classify it.
                  lineType: line.menuItemId ? 'FOOD' : 'SERVICE',
                  referenceId: line.menuItemId || undefined,
                  description: line.description.trim(),
                  quantity: Number(line.quantity || 1),
                  unit: line.unit,
                  unitPrice: Number(line.unitPrice || 0),
                  taxRatePercent: Number(line.taxRatePercent || 0),
                  // Left out, the server follows however this company enters
                  // prices - the catalogue's own answer is only ever a
                  // starting point and was never actually sent before this,
                  // so GST was added on top of a rate that already carried
                  // it on every inclusive item billed through this screen.
                  priceInclusiveTax: line.priceInclusiveTax,
                })),
                invoiceNumber:
                  isAdmin && manualNumbering && invoiceNumber.trim()
                    ? invoiceNumber.trim()
                    : undefined,
            }
            if (invoice) {
              update.mutate({ id: invoice.id, body }, { onSuccess: onClose })
              return
            }
            create.mutate(body, { onSuccess: onClose })
          }}
        >
          {editing ? 'Save correction' : 'Create invoice'}
        </Button>
      </DialogActions>
    </>
  )
}

/** Cancelling keeps the record and the reason; financial documents are never deleted. */
export function CancelInvoiceDialog({
  invoice,
  onClose,
}: {
  invoice: Invoice | null
  onClose: () => void
}) {
  const cancel = useCancelInvoice()
  const [reason, setReason] = useState('')

  if (!invoice) return null

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel {invoice.invoiceNumber}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={cancel.error} />
        <Alert severity="warning" sx={{ mb: 2 }}>
          The invoice stays on record with the reason and an audit entry — it is never deleted. Issue a
          credit note instead if money has already been received against it.
        </Alert>
        <TextField
          fullWidth
          required
          multiline
          minRows={2}
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Keep it</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!reason.trim() || cancel.isPending}
          onClick={() =>
            cancel.mutate({ id: invoice.id, reason: reason.trim() }, { onSuccess: onClose })
          }
        >
          Cancel invoice
        </Button>
      </DialogActions>
    </Dialog>
  )
}
